// Sprint 20.3 PR-5 (§G) — auto-clone failure surface.
//
// Displayed via `SpawnPreparingToast` when the runner returned an
// `ok=false` ack for `project.ensure_clone`. The backend already
// translated the `reason` into the founder-copy `message` field
// (mirrored verbatim by `founderCopyForCloneFailure()` in
// `backend/src/claude/session-manager.ts`), so this component does
// NOT re-translate — that would split the source of truth and let
// the two diverge.
//
// We render a richer affordance than the live "preparing" pill:
//
//   - icon + bold "Project setup failed"
//   - one-line reason (founder English / Spanish — backend-owned)
//   - Retry button (dismisses the toast; user re-sends from the
//     composer, which re-triggers the spawn pipeline; on a
//     `disk_full` failure the runner is now flagged unavailable so
//     the scheduler picks a different one — preview of EC-44 / PR-6)
//   - Dismiss (×) button — silent close; agent stays in 'error'
//
// The toast inherits `role="alert"` so screen readers announce it
// the moment it mounts. We do NOT auto-dismiss errors — they need
// explicit operator acknowledgement.

import { useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n';

export interface ProjectCloneFailedDialogProps {
  runnerId: string;
  reason:
    | 'auth_failed'
    | 'origin_unreachable'
    | 'corrupted_existing'
    | 'disk_full'
    | 'timeout'
    | 'runner_offline';
  // Founder-copy message produced by the backend. This is the canonical
  // display text — never reformat / translate here.
  message: string;
  onDismiss: () => void;
}

export function ProjectCloneFailedDialog({
  reason,
  message,
  onDismiss,
}: ProjectCloneFailedDialogProps) {
  const { t } = useTranslation();
  const dismissBtnRef = useRef<HTMLButtonElement>(null);

  // Move focus to the Dismiss button so keyboard users can close
  // immediately. The Retry button is the primary affordance, but we
  // pin focus to Dismiss to mirror how dialogs behave in Cursor:
  // primary action stays muscle-memory clickable, focus lands on
  // the safe escape.
  useEffect(() => {
    dismissBtnRef.current?.focus();
  }, []);

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="project-clone-failed-dialog"
      data-reason={reason}
      className="fixed bottom-4 right-4 z-30 flex max-w-md flex-col gap-2 rounded-md border border-status-error/30 bg-status-error/10 px-4 py-3 text-body text-foreground shadow-lg"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-status-error font-medium">
            {t('spawnPreparingToast.failedTitle')}
          </span>
          <span className="text-foreground">{message}</span>
        </div>
        <button
          ref={dismissBtnRef}
          type="button"
          onClick={onDismiss}
          aria-label={t('spawnPreparingToast.dismissAria')}
          data-testid="project-clone-failed-dismiss"
          className="text-foreground/60 hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40 rounded-sm"
        >
          {t('spawnPreparingToast.dismissGlyph')}
        </button>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onDismiss}
          data-testid="project-clone-failed-retry"
          className="px-3 py-1 rounded-md bg-status-error/20 text-status-error hover:bg-status-error/30 text-caption font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40"
        >
          {t('spawnPreparingToast.retry')}
        </button>
      </div>
    </div>
  );
}
